import { useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Outlet, useLocation, useMatch } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Icon } from '@column/column-ui-kit';

import { CustomPageContext, TabbedPage } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { PageHeader } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useNavigate } from '~/lib/navigation';
import { EntityRepository, EntityWithDetails } from '~/repositories/EntityRepository';
import { useHelpSidebarStore } from '~/stores/HelpSidebar';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { ApiError, EntityUpdateBusinessEntityBody, EntityUpdatePersonEntityBody } from '~/typings/API';
import { ApiErrCode } from '~/typings/enum';

import { Mode, RootEntityOutletContext } from './_util/RootEntityContext';
import {
  BeneficialOwnerFormValues,
  BusinessEntityFormValues,
  EntityFormType,
  PersonEntityFormValues,
  applyBusinessDefaults,
  applyPersonDefaults,
  buildBusinessRequest,
  buildPersonRequest,
  createDefaultBusinessFormValues,
  createDefaultPersonFormValues,
  createInitialBusinessFormState,
  useEntityForm,
} from './_util/useEntityForm';
import { EntityTypes } from './Edit';

const HelpButton = styled(Button)`
  padding: 0;
`;

const shouldNotRetryOnNotFound = (failureCount: number, error: unknown) => {
  const apiError = error as Partial<ApiError> | undefined;
  if (apiError?.code === ApiErrCode.ResourceNotFound.toString()) {
    return false;
  }
  return failureCount < 3;
};

const getPersonFormValues = (entity: EntityWithDetails | null): PersonEntityFormValues => {
  if (!entity?.personDetails) {
    return applyPersonDefaults(createDefaultPersonFormValues());
  }

  return applyPersonDefaults({
    ...entity.personDetails,
    id: entity.id,
  });
};

const getBusinessFormValues = (entity: EntityWithDetails | null): BusinessEntityFormValues => {
  if (!entity?.businessDetails) {
    return applyBusinessDefaults(createDefaultBusinessFormValues());
  }

  return applyBusinessDefaults({
    ...entity.businessDetails,
    id: entity.id,
    beneficialOwners: (entity.businessDetails?.beneficialOwners ?? []).map((owner) => ({
      ...owner,
    })) as BeneficialOwnerFormValues[],
  });
};

export const PageRootEntitySection: FC<CustomPageContext> = ({ entries }) => {
  const { currentPlatform, isSandbox, isAuthorized } = useSessionStore();
  const openHelpSidebar = useHelpSidebarStore((state) => state.openHelpSidebar);
  const navigate = useNavigate();
  const location = useLocation();
  const isLiveRoute = useMatch(ROUTE.ENTITIES_ROOT_ENTITY_LIVE);
  const addSuccessNotification = useNotificationStore((state) => state.addSuccessNotification);
  const addDangerNotification = useNotificationStore((state) => state.addDangerNotification);
  const { handleSubmitErrors } = useFormErrorHandler();

  const mode = useMemo(() => (isLiveRoute ? Mode.Live : Mode.Sandbox), [isLiveRoute]);
  const [formType, setFormType] = useState<EntityTypes>();

  const {
    data: sandboxRootEntity,
    isLoading: isSandboxLoading,
    refetch: refetchSandbox,
  } = useQuery<EntityWithDetails | null>({
    queryKey: [currentPlatform?.id, 'platform', 'root-entity', true],
    queryFn: () => EntityRepository.getRoot(currentPlatform?.id ?? '', 'sandbox'),
    enabled: !!currentPlatform?.id,
    staleTime: 5 * 60 * 1000,
    retry: shouldNotRetryOnNotFound,
  });

  const {
    data: liveRootEntity,
    isLoading: isLiveLoading,
    refetch: refetchLive,
  } = useQuery<EntityWithDetails | null>({
    queryKey: [currentPlatform?.id, 'platform', 'root-entity', 'live'],
    queryFn: () => EntityRepository.getRoot(currentPlatform?.id ?? '', 'live'),
    enabled: !!currentPlatform?.id && !!currentPlatform?.isLiveEnabled,
    staleTime: 5 * 60 * 1000,
    retry: shouldNotRetryOnNotFound,
  });

  const rootEntityResponse = mode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
  const form = useEntityForm(formType);

  const currentRootType = useMemo<EntityTypes | undefined>(() => {
    if (rootEntityResponse?.personDetails) return EntityFormType.Person;
    if (rootEntityResponse?.businessDetails) return EntityFormType.Business;
    return undefined;
  }, [rootEntityResponse]);

  const resetForm = useCallback(
    (entityType: EntityTypes, entity: EntityWithDetails | null) => {
      if (entity) {
        const values =
          entityType === EntityFormType.Person ? getPersonFormValues(entity) : getBusinessFormValues(entity);
        form.reset(values);
      } else {
        const values =
          entityType === EntityFormType.Person ? createDefaultPersonFormValues() : createInitialBusinessFormState();
        form.reset(values);
      }
    },
    [form]
  );

  useEffect(() => {
    if (location.pathname === ROUTE.ENTITIES_ROOT_ENTITY) {
      navigate(isSandbox ? ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX : ROUTE.ENTITIES_ROOT_ENTITY_LIVE, { replace: true });
    }
  }, [isSandbox, location.pathname, navigate]);

  useEffect(() => {
    if (currentRootType && currentRootType !== formType) {
      setFormType(currentRootType);
    }
  }, [currentRootType, formType]);

  const refetchCurrent = async () => {
    if (mode === Mode.Sandbox) {
      await refetchSandbox();
    } else {
      await refetchLive();
    }
  };

  const handleSubmit = form.handleSubmit(async () => {
    if (!formType) {
      addDangerNotification({ content: 'Please select an entity type.' });
      return;
    }

    try {
      if (formType === EntityFormType.Person) {
        const payload = buildPersonRequest(form.getValues() as PersonEntityFormValues);

        if (rootEntityResponse?.personDetails && rootEntityResponse.id) {
          const response = await EntityRepository.updatePerson(
            rootEntityResponse.id,
            payload as EntityUpdatePersonEntityBody
          );
          addSuccessNotification({
            content: `${response.personDetails?.firstName ?? ''} ${response.personDetails?.lastName ?? ''}`.trim()
              ? `${response.personDetails?.firstName ?? ''} ${response.personDetails?.lastName ?? ''} updated`
              : 'Root entity updated',
          });
          await refetchCurrent();
        } else {
          const response = await EntityRepository.createRootPerson(
            currentPlatform?.id ?? '',
            payload,
            mode === Mode.Sandbox ? 'sandbox' : 'live'
          );
          addSuccessNotification({
            content: `${response.personDetails?.firstName ?? ''} ${response.personDetails?.lastName ?? ''}`.trim()
              ? `${response.personDetails?.firstName ?? ''} ${response.personDetails?.lastName ?? ''} created`
              : 'Root entity created',
          });
          await refetchCurrent();
          setFormType(EntityFormType.Person);
        }
      } else {
        const payload = buildBusinessRequest(form.getValues() as BusinessEntityFormValues);

        if (rootEntityResponse?.businessDetails && rootEntityResponse.id) {
          const response = await EntityRepository.updateBusiness(
            rootEntityResponse.id,
            payload as EntityUpdateBusinessEntityBody
          );
          addSuccessNotification({
            content: response.businessDetails?.businessName
              ? `${response.businessDetails.businessName} updated`
              : 'Root entity updated',
          });
          await refetchCurrent();
        } else {
          const response = await EntityRepository.createRootBusiness(
            currentPlatform?.id ?? '',
            payload,
            mode === Mode.Sandbox ? 'sandbox' : 'live'
          );
          addSuccessNotification({
            content: response.businessDetails?.businessName
              ? `${response.businessDetails.businessName} created`
              : 'Root entity created',
          });
          await refetchCurrent();
          setFormType(EntityFormType.Business);
        }
      }
    } catch (error: any) {
      const message = error?.message ?? 'Failed to save root entity';
      addDangerNotification({ content: message });
    }
  }, handleSubmitErrors);

  const headerRootEntity = mode === Mode.Sandbox ? sandboxRootEntity : liveRootEntity;
  const headerHasRootEntity = Boolean(headerRootEntity);

  const tabEntries = [
    {
      label: 'Live',
      path: ROUTE.ENTITIES_ROOT_ENTITY_LIVE,
    },
    {
      label: 'Sandbox',
      path: ROUTE.ENTITIES_ROOT_ENTITY_SANDBOX,
    },
  ];

  return (
    <FormProvider {...form}>
      <PageHeader
        text="Root Entity"
        decoration={
          <HelpButton
            icon={<Icon.CircleQuestionmark />}
            onlyIcon
            onClick={() => openHelpSidebar('RootEntity')}
            variant="subtle"
            size="small"
          />
        }
        backPath={ROUTE.ENTITIES}
        tabs={<TabbedPage entries={entries ?? tabEntries} />}
      >
        {mode === Mode.Sandbox && (
          <Button
            onClick={handleSubmit}
            isDisabled={!isAuthorized({ permission: 'entities', permissionLevel: 'write' })}
            size="small"
            icon={!headerHasRootEntity && <Icon.Plus />}
          >
            {headerHasRootEntity ? 'Save Changes' : 'Create Root Entity'}
          </Button>
        )}
      </PageHeader>
      <Outlet
        context={
          {
            mode,
            formType,
            sandboxRootEntity,
            liveRootEntity,
            isSandboxLoading,
            isLiveLoading,
            isSandbox: isSandbox ?? false,
            handleSubmit,
            setFormType,
            resetForm,
          } satisfies RootEntityOutletContext
        }
      />
    </FormProvider>
  );
};
